/**
 * Speaking into the input box.
 *
 * ── Two engines, and why there have to be two ────────────────────────────────
 *
 * Chrome, Edge and Safari ship speech recognition in the browser. It is free,
 * it needs no network round trip of ours, and it returns INTERIM results —
 * revised guesses that arrive while the word is still being said. That last
 * part is the whole feature: "real time" means words appearing as you speak
 * them, and nothing that has to record a phrase, upload it and wait for a model
 * can do that. So where it exists, it is used, and this file's server half
 * never runs.
 *
 * Firefox does not ship it, and no flag turns it on. For those browsers the
 * fallback records the microphone, cuts it at the silences between phrases, and
 * posts each phrase to `/api/transcribe/live`. Text arrives a second or two
 * after each phrase rather than during it — worse, but the difference between a
 * feature and a dead button.
 *
 * Both are hidden behind `startLiveSpeech`, so the button never knows which one
 * it started. The one place the difference is visible is deliberate: the button
 * says "listening" for one and "transcribing" for the other, because a user
 * watching nothing appear for two seconds deserves to know something is
 * happening.
 *
 * ── Interim text is not final text ───────────────────────────────────────────
 *
 * `onPartial` is a guess that will be REPLACED, possibly several times, and
 * possibly by nothing. `onFinal` is a committed run that will never be revised.
 * The caller has to treat them differently — see the dictation anchor in
 * Translator.tsx — because inserting partials as if they were final is how
 * dictation ends up stuttering the same half-word three times into the box.
 */

import { transcribeClip } from './api';

export type LiveState =
  /** Microphone opening, permission possibly being asked for. */
  | 'starting'
  /** Open and hearing. */
  | 'listening'
  /** Fallback only: a phrase is with the server. */
  | 'thinking'
  | 'stopped';

/**
 * Whose fault a fatal error is, which decides whether trying the OTHER engine
 * could possibly help.
 *
 *   • `service`  — this recogniser cannot do the job: unreachable, refusing,
 *                  dying on start. Our own route is a real alternative.
 *   • `permission` / `device` — the microphone itself. Both engines need the
 *                  same one, so switching would fail identically and only waste
 *                  the user's time.
 */
export type ErrorCause = 'service' | 'permission' | 'device';

export interface LiveSpeechHandlers {
  /** A revisable guess. Replaces whatever the previous partial put on screen. */
  onPartial: (text: string) => void;
  /** Committed words. Appended; never taken back. */
  onFinal: (text: string) => void;
  onState: (state: LiveState) => void;
  /** 0..1, for the level meter. Both engines emit it; only one measures it. */
  onLevel: (level: number) => void;
  /**
   * `fatal` means the microphone is now closed and the button must go back to
   * its resting state. A non-fatal message is worth showing but dictation
   * carries on — one phrase that failed to transcribe is not the end of the
   * sentence someone is in the middle of.
   */
  onError: (message: string, fatal: boolean, cause?: ErrorCause) => void;
  /**
   * The language the speech turned out to be in, once it is known from the
   * audio. Only the server engine can answer this — a browser recogniser is
   * told a language and never discovers one — and it is what lets dictation
   * hand itself over to the free, word-by-word engine after the first phrase.
   */
  onLanguage?: (code: string, confidence: number) => void;
}

export interface LiveSpeechOptions extends LiveSpeechHandlers {
  /**
   * BCP-47 tag to recognise.
   *
   * ⚠️ EMPTY IS MEANINGFUL, and it is not "use the browser's default". It means
   * nobody knows yet what language is about to be spoken — which decides which
   * engine runs, because only one of them can find out. See `startLiveSpeech`.
   */
  lang: string;
  /**
   * Whether our own transcription route is available to answer that question.
   * False switches the browser engine back on as a last resort, guessing.
   */
  serverAvailable?: boolean;
  /**
   * Use our route even though the language is known — set once the browser's
   * own recogniser has proved unusable, so a broken speech service degrades to
   * slower dictation rather than to none.
   */
  forceServer?: boolean;
}

export interface LiveSpeech {
  stop: () => void;
  readonly kind: 'browser' | 'server';
}

/* ── Which engine ───────────────────────────────────────────────────────────*/

function recognitionCtor(): any {
  if (typeof window === 'undefined') return null;
  return (window as any).SpeechRecognition ?? (window as any).webkitSpeechRecognition ?? null;
}

export function liveSpeechKind(): 'browser' | 'server' | 'none' {
  if (typeof window === 'undefined') return 'none';
  if (recognitionCtor()) return 'browser';
  // `mediaDevices` is undefined outside a secure context, which is the case
  // this guard is really for: a browser that could record, on http.
  if (typeof MediaRecorder !== 'undefined' && typeof navigator.mediaDevices?.getUserMedia === 'function') {
    return 'server';
  }
  return 'none';
}

/**
 * The language tag to recognise in.
 *
 * Recognition is not detection: the engine is told what to expect and matches
 * against that language's sounds, so an English hint on Korean speech produces
 * confident nonsense rather than a low-confidence guess. Getting this from the
 * source picker is therefore load-bearing rather than a nicety.
 *
 * A REGION is appended where one is known, because the tags differ in what they
 * are actually good at — `en-US` and `en-GB` are different acoustic models, and
 * `zh` alone is ambiguous between Mandarin and Cantonese in a way `zh-CN` is
 * not. `countries` comes from the language registry, which orders it
 * most-associated first for exactly this kind of use.
 *
 * The exceptions below are the languages whose registry entry cannot supply the
 * region, or supplies the wrong one: English lists `gb` first while the app's
 * own dictation default should match its American-slang mode.
 *
 * `ko: 'KR'` is in the list for a reason worth keeping written down. Korean
 * used to list no country at all, so this was the only thing standing between
 * dictation and a bare `ko` tag; the registry now lists `kr` like every other
 * language and the flag itself is a per-account SETTING (Settings ▸ Flags).
 * This entry is therefore redundant today and is deliberately kept: it means
 * somebody who switches the Korean flag off is changing what is DRAWN and
 * nothing else — the recogniser keeps being told `ko-KR` either way.
 */
const REGION: Record<string, string> = {
  ko: 'KR', en: 'US', zh: 'CN', ja: 'JP', he: 'IL', el: 'GR', cs: 'CZ', da: 'DK',
  et: 'EE', ga: 'IE', ka: 'GE', hy: 'AM', sv: 'SE', uk: 'UA', vi: 'VN', fa: 'IR',
  ur: 'PK', hi: 'IN', bn: 'BD', ta: 'IN', te: 'IN', ms: 'MY', sr: 'RS', sl: 'SI',
};

export function speechLang(code: string, countries: string[] = []): string {
  if (!code || code === 'auto') return '';
  const region = REGION[code] ?? countries[0]?.toUpperCase();
  return region ? `${code}-${region}` : code;
}

/* ── Engine 1: the browser's own ────────────────────────────────────────────*/

/**
 * How long the microphone may stay open with nothing said into it.
 *
 * Not a cost control — this engine is free — but an open microphone is a thing
 * people forget about, and a browser tab quietly listening to a room for an
 * afternoon is not something to ship on purpose.
 */
const IDLE_STOP_MS = 90_000;

function startBrowserEngine(opts: LiveSpeechOptions): LiveSpeech {
  const Recognition = recognitionCtor();
  const recognition = new Recognition();
  recognition.continuous = true;
  recognition.interimResults = true;
  if (opts.lang) recognition.lang = opts.lang;

  let stopping = false;
  let running = false;
  /**
   * Sessions that began and ended again immediately, having heard nothing.
   *
   * `onend` firing straight back into `start()` is the normal way to keep a
   * continuous session alive — the engine ends the session itself after a pause
   * and no setting prevents it. The danger is a `start()` that fails instantly
   * and permanently (service refused, no network): end follows start with
   * nothing in between, and a naive restart is then a hot loop.
   *
   * ⚠️ What this must NOT count is a quiet room. The first version counted every
   * result-less session, and Chrome ends one with `no-speech` every time a
   * pause outlasts its patience — so pressing the mic and taking a few seconds
   * to think produced "Dictation could not be started" while the microphone was
   * working perfectly. Hence both guards below: a round only counts if the
   * session died FAST (a working one lasts seconds, waiting for speech) and
   * without the engine saying it was merely quiet. Prolonged silence is
   * `IDLE_STOP_MS`'s job, and it says something true.
   */
  let deadRounds = 0;
  let startedAt = 0;
  let quietRound = false;
  /** How much of this session's cumulative result list has been handed over. */
  let delivered = 0;
  let lastHeard = Date.now();
  let idleTimer: ReturnType<typeof setInterval> | null = null;

  /**
   * The meter, without a microphone.
   *
   * This engine deliberately does NOT open its own `getUserMedia` stream to
   * measure a real level. It would be a second capture of the same device
   * purely for decoration, and on some Android builds a held stream stops the
   * recogniser hearing anything at all — a broken feature in exchange for a
   * moving bar. So the bar is driven by recognition activity instead: it lifts
   * when words arrive and falls back on its own.
   */
  let level = 0;
  const decay = setInterval(() => {
    level = Math.max(0, level - 0.08);
    opts.onLevel(level);
  }, 90);
  const pulse = () => { level = Math.min(1, level + 0.55); opts.onLevel(level); };

  recognition.onstart = () => {
    running = true;
    startedAt = Date.now();
    quietRound = false;
    // `results` is per-session and starts empty again after every restart, so
    // the high-water mark has to start again with it.
    delivered = 0;
    opts.onState('listening');
  };

  /**
   * Words from the engine.
   *
   * ⚠️ `event.resultIndex` is deliberately IGNORED, and that is the fix for
   * dictation that repeats itself on anything long. `results` is cumulative for
   * the session — finalised entries stay in it — and `resultIndex` is supposed
   * to point at the first entry that changed. In continuous mode Chrome does
   * not reliably advance it; when it comes back as 0, reading from there
   * re-delivers every sentence already in the box, and a long dictation ends up
   * with each sentence written two or three times.
   *
   * A high-water mark cannot go wrong that way: entry `i` is handed over once,
   * whatever index the event claims changed.
   */
  recognition.onresult = (event: any) => {
    lastHeard = Date.now();
    deadRounds = 0;
    pulse();

    let interim = '';
    for (let i = 0; i < event.results.length; i++) {
      const result = event.results[i];
      const text = String(result[0]?.transcript ?? '');
      if (!result.isFinal) {
        interim += text;
        continue;
      }
      if (i < delivered) continue;
      delivered = i + 1;
      const trimmed = text.trim();
      if (trimmed) opts.onFinal(trimmed);
    }
    // Always emitted, including empty: the partial that a final has just
    // replaced has to be cleared off the screen, and "no interim in this event"
    // is exactly how the engine says so.
    opts.onPartial(interim.trim());
  };

  /**
   * Give up, for a reason worth telling the user.
   *
   * Every fatal path goes through here, and it exists because the first version
   * had them scattered — each one showing a message and cleaning up its timers
   * while leaving the RECOGNISER running and `stopping` false. A pending
   * `restart` timer would then fire, `onstart` would report "listening", and
   * the button went back to lit with an error underneath it. Worse, the button
   * was then dead: the engine had already been discarded, so pressing it did
   * nothing at all and only a page reload recovered.
   *
   * So: raise the flag FIRST, so nothing restarts behind us; abort rather than
   * stop, because there is nothing worth finalising; then clean up and report.
   */
  function fail(message: string, cause: ErrorCause = 'service') {
    if (stopping) return;
    stopping = true;
    try { recognition.abort(); } catch { /* never started */ }
    cleanUp();
    opts.onError(message, true, cause);
    opts.onState('stopped');
  }

  recognition.onerror = (event: any) => {
    const code = String(event?.error ?? '');
    switch (code) {
      case 'no-speech':
        // Not an error, and not a failure to start. The engine says this every
        // time a pause outlasts its patience; `onend` follows and the session
        // restarts. Flagged so `onend` knows this round was merely quiet.
        quietRound = true;
        return;
      case 'aborted':
        // Ours, from stop() or fail().
        return;
      case 'not-allowed':
      case 'service-not-allowed':
        fail('The microphone is blocked for this site. Allow it in your browser’s address bar, then press the mic again.', 'permission');
        return;
      case 'audio-capture':
        fail('No microphone was found.', 'device');
        return;
      case 'network':
        fail('Speech recognition could not reach the network.');
        return;
      case 'language-not-supported':
        // Not shown to anyone in practice: `service` sends this straight to our
        // own route, which recognises every language and needs no tag.
        fail('This browser cannot recognise that language.');
        return;
      default:
        fail('Dictation stopped unexpectedly.');
    }
  };

  recognition.onend = () => {
    running = false;
    if (stopping) {
      cleanUp();
      opts.onState('stopped');
      return;
    }
    // Did this session die on the spot, or did it simply run out of things to
    // hear? A working recogniser waiting for speech lasts seconds; one that is
    // refused comes back within a few hundred milliseconds. Only the second is
    // a reason to stop trying.
    const instant = Date.now() - startedAt < 1_200;
    if (instant && !quietRound) deadRounds++;
    else deadRounds = 0;

    if (deadRounds > 6) {
      fail('Dictation could not be started. Try again in a moment.');
      return;
    }
    restart();
  };

  /**
   * Begin the next session, retrying once if the engine says it is still busy.
   *
   * `start()` throws `InvalidStateError` when the previous session has not
   * finished tearing down, and the window in which that is true is short but
   * real — `onend` is where a restart has to be attempted, and it fires at the
   * beginning of that teardown rather than the end of it. Swallowing the throw
   * silently ends the dictation while the button is still lit, which is the
   * failure this exists to prevent; the retry costs one timer.
   */
  function restart(attempt = 0) {
    if (stopping) return;
    try {
      recognition.start();
    } catch {
      if (attempt < 3) setTimeout(() => restart(attempt + 1), 120);
      else fail('Dictation stopped unexpectedly.');
    }
  }

  idleTimer = setInterval(() => {
    if (Date.now() - lastHeard < IDLE_STOP_MS) return;
    fail('Dictation stopped after a minute and a half of silence.');
  }, 5_000);

  function cleanUp() {
    clearInterval(decay);
    if (idleTimer) { clearInterval(idleTimer); idleTimer = null; }
    opts.onLevel(0);
  }

  opts.onState('starting');
  restart();

  return {
    kind: 'browser',
    stop() {
      stopping = true;
      // `stop()` rather than `abort()`: it lets the engine finalise the words
      // already in its buffer, so releasing the button mid-sentence keeps that
      // sentence instead of discarding it.
      try { running ? recognition.stop() : recognition.abort(); } catch { /* already stopped */ }
      cleanUp();
      opts.onState('stopped');
    },
  };
}

/* ── Engine 2: the microphone, cut at the silences ──────────────────────────*/

/** How quiet counts as quiet. RMS on a 0..1 scale; ordinary speech peaks ~0.1. */
const SILENCE_RMS = 0.012;
/** Silence this long after speech ends the phrase and sends it. */
const SILENCE_HOLD_MS = 800;
/** A phrase shorter than this is a cough, a door, a keyboard. Not sent. */
const MIN_PHRASE_MS = 400;
/** Held-down speech is cut here anyway, matching the server's own ceiling. */
const MAX_PHRASE_MS = 25_000;
/**
 * A recorder that has heard nothing for this long is restarted.
 *
 * Not about audio quality — about memory. The recorder buffers everything since
 * it started, and only the first blob of a session carries the container
 * header, so the buffer cannot be dropped without restarting the recorder too.
 * Left alone, a microphone open in a quiet room accumulates that silence for as
 * long as it is open.
 */
const STALE_SILENCE_MS = 4_000;
/**
 * The longest one press of the button may dictate for.
 *
 * This engine spends real money per phrase, and a microphone nobody closes is
 * the shape of that spending nobody notices. Ninety seconds of no speech would
 * not catch it: the failure is a live room, not a quiet one.
 */
const MAX_SESSION_MS = 5 * 60_000;

/** The first container the browser will actually record, and its extension. */
function pickMime(): { mime: string; ext: string } | null {
  const candidates: Array<{ mime: string; ext: string }> = [
    { mime: 'audio/webm;codecs=opus', ext: 'webm' },
    { mime: 'audio/webm', ext: 'webm' },
    { mime: 'audio/ogg;codecs=opus', ext: 'ogg' },
    { mime: 'audio/mp4', ext: 'm4a' },
  ];
  for (const c of candidates) {
    if (MediaRecorder.isTypeSupported?.(c.mime)) return c;
  }
  // Safari once supported recording without advertising a type. An empty string
  // means "your default", which ffmpeg will identify from the bytes anyway.
  return { mime: '', ext: 'webm' };
}

async function startServerEngine(opts: LiveSpeechOptions): Promise<LiveSpeech> {
  opts.onState('starting');

  let stream: MediaStream;
  try {
    stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        // On by default in Chrome, off in Firefox — which is the browser this
        // engine exists for, and the one where a quietly-recorded phrase comes
        // back as an empty transcript.
        autoGainControl: true,
      },
    });
  } catch (err) {
    const name = (err as Error)?.name;
    opts.onState('stopped');
    opts.onError(
      name === 'NotAllowedError' || name === 'SecurityError'
        ? 'The microphone is blocked for this site. Allow it in your browser’s address bar, then press the mic again.'
        : name === 'NotFoundError'
          ? 'No microphone was found.'
          : 'The microphone could not be opened.',
      true,
      name === 'NotAllowedError' || name === 'SecurityError' ? 'permission' : 'device',
    );
    return { kind: 'server', stop() { /* nothing was opened */ } };
  }

  const chosen = pickMime();
  const context: AudioContext = new (window.AudioContext ?? (window as any).webkitAudioContext)();
  const analyser = context.createAnalyser();
  analyser.fftSize = 1024;
  // A short window: this is a speech/no-speech decision made twenty times a
  // second, and a long average would smear the gap between two phrases into a
  // single continuous one.
  analyser.smoothingTimeConstant = 0.2;
  context.createMediaStreamSource(stream).connect(analyser);
  const samples = new Float32Array(analyser.fftSize);

  let recorder: MediaRecorder | null = null;
  let buffer: BlobPart[] = [];
  let recordingSince = 0;
  let hadSpeech = false;
  let quietSince = Date.now();
  /**
   * Two flags, not one, and the difference between them is the last phrase.
   *
   * `stopping` is set the instant the button is pressed: the microphone closes
   * then, and nothing may report itself as listening afterwards. `stopped` is
   * set only once the phrase that was in flight at that moment has come back —
   * because releasing the button at the end of a sentence must not discard that
   * sentence, and the transcript of it is still allowed to reach the box.
   */
  let stopping = false;
  let stopped = false;
  /** A `cut()` whose final blob has not reached the queue yet — see `cut`. */
  let awaitingCut = false;
  const startedAt = Date.now();

  /* One clip on the wire at a time, so the words arrive in the order they were
     spoken. Two concurrent posts would be faster and would occasionally deliver
     the second sentence first, which is worse than the wait. */
  let queue: Promise<void> = Promise.resolve();
  let pending = 0;

  function send(blob: Blob, ms: number) {
    pending++;
    if (!stopping) opts.onState('thinking');
    queue = queue.then(async () => {
      try {
        const clip = await transcribeClip(blob, chosen?.ext ?? 'webm', ms);
        // Guarded on `stopped`, not `stopping`: a phrase finished before the
        // button was pressed still belongs in the box.
        if (!stopped && clip.text) opts.onFinal(clip.text);
        /*
         * What language that was, if the transcript said so plainly enough.
         *
         * Reported after the words, never instead of them — this hands the rest
         * of the dictation to a different engine, and doing that before the
         * phrase had landed would drop it.
         *
         * The 0.6 floor is the point of the whole exercise. A language settled
         * by its script comes back at high confidence and cannot be wrong; a
         * Latin-script guess from a handful of function words can be, and
         * acting on a weak one would pin the browser engine to the wrong
         * language — which is exactly the failure being fixed. Below the floor
         * we simply stay here, keep transcribing what we hear, and ask again on
         * the next phrase.
         */
        if (!stopped && clip.lang && (clip.confidence ?? 0) >= 0.6) {
          opts.onLanguage?.(clip.lang, clip.confidence ?? 0);
        }
      } catch (err) {
        const message = (err as Error).message || 'That phrase could not be transcribed.';
        // A quota refusal closes the microphone; anything else is one lost
        // phrase in a sentence the user is still speaking.
        const fatal = (err as any).stop === true;
        if (fatal) {
          // Not `finish()`: there is nothing to flush, and the clips already
          // queued behind this one would be refused for the same reason. The
          // resting state has to be announced from here, because the loop that
          // would otherwise have announced it has just been cancelled.
          closeMic();
          stopped = true;
          opts.onState('stopped');
        }
        if (!stopped || fatal) opts.onError(message, fatal);
      } finally {
        pending--;
        if (!stopping && pending === 0) opts.onState('listening');
      }
    });
  }

  /**
   * End the current recorder session.
   *
   * `keep` decides whether what it captured is sent; `reopen` whether another
   * session starts behind it. Both are false when the accumulated audio is
   * silence being thrown away at the end of a dictation.
   */
  function cut(keep: boolean, reopen = true): boolean {
    const active = recorder;
    if (!active || active.state === 'inactive') return false;
    const ms = Date.now() - recordingSince;
    // `stop()` does not deliver the last blob synchronously — `onstop` is a
    // task, and `send` therefore runs a tick or two later. Anything waiting for
    // the queue to empty has to know a clip is still on its way to it, or it
    // will find `pending === 0` and conclude there is nothing left.
    awaitingCut = true;
    active.onstop = () => {
      awaitingCut = false;
      const blob = new Blob(buffer, chosen?.mime ? { type: chosen.mime } : undefined);
      buffer = [];
      if (keep && blob.size > 0 && ms >= MIN_PHRASE_MS) send(blob, ms);
      if (reopen && !stopping) openRecorder();
    };
    hadSpeech = false;
    active.stop();
    return true;
  }

  function openRecorder() {
    try {
      recorder = chosen?.mime
        ? new MediaRecorder(stream, { mimeType: chosen.mime })
        : new MediaRecorder(stream);
    } catch {
      closeMic();
      stopped = true;
      opts.onState('stopped');
      opts.onError('This browser could not record audio.', true);
      return;
    }
    buffer = [];
    recordingSince = Date.now();
    hadSpeech = false;
    recorder.ondataavailable = (e) => { if (e.data.size) buffer.push(e.data); };
    // A timeslice rather than one blob at the end: without it, `stop()` is the
    // only thing that ever produces data, and a browser that drops the stream
    // mid-phrase takes the phrase with it.
    recorder.start(250);
  }

  const loop = setInterval(() => {
    analyser.getFloatTimeDomainData(samples);
    let sum = 0;
    for (let i = 0; i < samples.length; i++) sum += samples[i] * samples[i];
    const rms = Math.sqrt(sum / samples.length);
    // The meter is logarithmic because hearing is: on a linear scale ordinary
    // speech sits in the bottom tenth of the bar and the bar looks broken.
    opts.onLevel(Math.min(1, Math.max(0, (20 * Math.log10(rms || 1e-8) + 60) / 45)));

    const now = Date.now();
    if (rms > SILENCE_RMS) {
      hadSpeech = true;
      quietSince = now;
    }

    if (now - startedAt > MAX_SESSION_MS) {
      finish();
      opts.onError('Dictation stops after five minutes. Press the mic again to carry on.', true);
      return;
    }
    if (hadSpeech && now - quietSince > SILENCE_HOLD_MS) { cut(true); return; }
    if (hadSpeech && now - recordingSince > MAX_PHRASE_MS) { cut(true); return; }
    // Nothing said for a while: throw the accumulated silence away.
    if (!hadSpeech && now - recordingSince > STALE_SILENCE_MS) cut(false);
  }, 50);

  /** Release the hardware. Nothing about the queue — see `finish`. */
  function closeMic() {
    stopping = true;
    clearInterval(loop);
    // The tracks first: they are the microphone indicator in the browser's tab
    // strip, and leaving one on for the length of a closing AudioContext reads
    // as still being recorded.
    stream.getTracks().forEach((t) => t.stop());
    void context.close().catch(() => { /* already closed */ });
    opts.onLevel(0);
  }

  /**
   * Stop, keeping the sentence in progress.
   *
   * The phrase being spoken at the moment the button is pressed has had no
   * trailing silence to cut it, so it has to be cut here — otherwise letting go
   * at the end of a sentence is the one gesture that reliably loses one. What
   * follows is a wait for the queue rather than an immediate `stopped`, so the
   * words arrive before the button says it has finished.
   */
  function finish() {
    if (stopping) return;
    const cutting = hadSpeech && cut(true, false);
    closeMic();
    if (!cutting && pending === 0) {
      stopped = true;
      opts.onState('stopped');
      return;
    }
    // Polled rather than chained off `queue`: the clip just cut is not IN the
    // queue yet (its `onstop` has not run), so a `.then` on the tail as it
    // stands right now would resolve before it was ever added.
    //
    // The deadline is the one thing here that is not about correctness. If a
    // recorder never fires `onstop` or a request hangs past its own timeout,
    // the button must still come back — a control stuck on "Writing…" with the
    // microphone already closed is the worst of both states.
    const deadline = Date.now() + 20_000;
    const settle = () => {
      if ((awaitingCut || pending > 0) && Date.now() < deadline) {
        setTimeout(settle, 120);
        return;
      }
      stopped = true;
      opts.onState('stopped');
    };
    settle();
  }

  openRecorder();
  opts.onState('listening');

  return { kind: 'server', stop: finish };
}

/* ── The one entry point ────────────────────────────────────────────────────*/

/**
 * Start dictating, on whichever engine can actually do the job asked of it.
 *
 * ── The rule, and the reason for it ──────────────────────────────────────────
 *
 * The browser's own recogniser is better in every way but one: it has to be
 * TOLD which language to expect and cannot discover it. Given the wrong one it
 * does not return a weak guess or nothing at all — it returns confident words
 * in the language it was told, so Korean speech comes back as fluent English
 * nonsense. And it cannot correct itself, because the only evidence of the
 * mistake is the gibberish it just produced.
 *
 * Our own route has the opposite shape. It costs money and answers a phrase at
 * a time rather than a word at a time, but it transcribes whatever it hears in
 * the script it was spoken in, and reports back what that was.
 *
 * So the language decides the engine:
 *
 *   • KNOWN  — the picker says so, or a phrase already told us → the browser's
 *              own engine, free and word by word.
 *   • UNKNOWN — our route, which is the only one that can find out.
 *
 * The unknown case is not permanent. The first phrase comes back with the
 * language attached, the caller passes it down, and dictation restarts on the
 * free engine for the rest of the session. One paid phrase buys the answer that
 * makes everything after it free.
 */
export async function startLiveSpeech(opts: LiveSpeechOptions): Promise<LiveSpeech | null> {
  const kind = liveSpeechKind();
  if (kind === 'none') {
    opts.onError('This browser cannot record speech.', true);
    return null;
  }

  // Somebody knows the language: use the good engine — unless it has already
  // shown it cannot work here.
  if (opts.lang && kind === 'browser' && !opts.forceServer) return startBrowserEngine(opts);

  // Nobody does. Our own route can hear what it is — provided it is switched on
  // and this browser can record at all.
  const canRecord = typeof MediaRecorder !== 'undefined'
    && typeof navigator.mediaDevices?.getUserMedia === 'function';
  if (opts.serverAvailable !== false && canRecord) return startServerEngine(opts);

  // Neither: dictation is switched off server-side and this browser can only
  // be told a language, not asked. Falling back to its own default is a guess,
  // and a guess is better than a dead button — it is right for the majority who
  // speak the language their browser is set to.
  if (kind === 'browser' && !opts.forceServer) return startBrowserEngine(opts);

  opts.onError('Dictation is not available right now.', true, 'service');
  return null;
}

/* ── Joining spoken text to what is already in the box ──────────────────────*/

/** Characters that write without spaces between words. */
const SCRIPTLESS_SPACING = /[　-〿぀-ヿ㐀-䶿一-鿿豈-﫿가-힯]/;
/** Punctuation that attaches to the word before it rather than starting one. */
const LEADING_PUNCT = /^[,.!?;:)\]}»”’…、。！？，]/;

/**
 * The space between one dictated run and the next — or the absence of one.
 *
 * Speech recognition returns phrases with no leading space, so something has to
 * put one there, and doing it unconditionally is wrong in both directions: it
 * would put a space before the comma an English engine returns as its own
 * segment, and it would put spaces through Korean and Japanese, which are
 * written without them and where a stray space changes where the words are.
 */
export function joinSpoken(before: string, next: string): string {
  if (!next) return '';
  if (!before) return next;
  const left = before.slice(-1);
  if (/\s/.test(left)) return next;
  if (LEADING_PUNCT.test(next)) return next;
  if (SCRIPTLESS_SPACING.test(left) && SCRIPTLESS_SPACING.test(next[0])) return next;
  return ` ${next}`;
}

/* ── Spoken commands ────────────────────────────────────────────────────────
 *
 * "Enter" and "new line" break the line instead of being written down. Without
 * them speech can only produce one unbroken paragraph, because a microphone has
 * no Return key — which matters most for the thing this app is for: pasting a
 * message, a list or a chat log into the box and having it translated with its
 * shape intact.
 *
 * ── The rule, and why it is the strict one ───────────────────────────────────
 *
 * A command fires ONLY when it is the whole utterance. "Press enter to
 * continue" and "I entered the room" are written down as spoken; a phrase said
 * on its own, with a pause either side, breaks the line.
 *
 * Wider rules were considered and rejected. Matching a trailing "enter" would
 * eat the last word of "I don't know what to enter"; matching anywhere would
 * eat it mid-sentence. The strict rule also happens to match how the engines
 * chunk speech — both of them finalise at pauses, and a pause is exactly what
 * someone leaves around a command — so it costs almost nothing in practice.
 *
 * ⚠️ It is not free, though, and the case it costs is real: this is a
 * TRANSLATOR, so dictating the single word "enter" to find out what it is in
 * Korean breaks a line instead. Typing it still works. That trade was made
 * deliberately, on the owner's request for the feature.
 *
 * ── Every language, not the recogniser's ─────────────────────────────────────
 *
 * The table is matched WHOLE regardless of which language the recogniser was
 * set to, because the two disagree constantly in real use: someone with the
 * source picker on English says "엔터" out of habit, and an engine set to
 * Korean hears an English "new line" perfectly well. A whole utterance that is
 * exactly one of these is a command in any of them, and nothing here collides
 * with an ordinary word of another language.
 */

/** Commands that insert one line break. */
const LINE_BREAK = [
  // English
  'enter', 'new line', 'newline', 'line break', 'next line', 'break line',
  // Korean
  '엔터', '엔터키', '줄바꿈', '줄 바꿈', '다음 줄', '새 줄', '개행',
  // Japanese
  '改行', 'エンター', '次の行', '新しい行',
  // Chinese
  '换行', '換行', '回车', '回車', '新行', '下一行',
  // Spanish
  'nueva línea', 'nueva linea', 'salto de línea', 'salto de linea',
  // French
  'nouvelle ligne', 'saut de ligne', 'retour à la ligne', 'retour a la ligne',
  // German
  'neue zeile', 'zeilenumbruch',
  // Portuguese
  'nova linha', 'quebra de linha',
  // Italian
  'nuova riga', 'a capo',
  // Dutch
  'nieuwe regel',
  // Polish
  'nowa linia', 'nowy wiersz',
  // Turkish
  'yeni satır', 'yeni satir',
  // Russian / Ukrainian
  'новая строка', 'перенос строки', 'новий рядок',
  // Arabic / Hebrew / Hindi / Vietnamese / Thai / Indonesian
  'سطر جديد', 'שורה חדשה', 'नई लाइन', 'नई पंक्ति', 'dòng mới', 'บรรทัดใหม่', 'baris baru',
];

/** Commands that insert a blank line — one paragraph to the next. */
const PARAGRAPH = [
  'new paragraph', 'next paragraph', 'paragraph break',
  '새 문단', '새 단락', '다음 문단', '문단 바꿈',
  '新しい段落', '新段落', '新的段落',
  'nuevo párrafo', 'nuevo parrafo',
  'nouveau paragraphe',
  'neuer absatz',
  'novo parágrafo', 'novo paragrafo',
  'nuovo paragrafo',
  'nieuwe alinea',
  'nowy akapit',
  'yeni paragraf',
  'новый абзац', 'новий абзац',
  'فقرة جديدة', 'פסקה חדשה', 'नया पैराग्राफ', 'đoạn mới', 'ย่อหน้าใหม่', 'paragraf baru',
];

/**
 * Reduce an utterance to the form the table is keyed on.
 *
 * Everything that is not a letter or a digit goes, which does three jobs at
 * once and is why it is one line rather than three: it drops the full stop the
 * recogniser puts on the end of "New line.", it makes case irrelevant, and it
 * erases the space in "new line" — so "newline", "new line" and "New-Line" are
 * one entry, and so are `줄바꿈` and `줄 바꿈`.
 */
function commandKey(text: string): string {
  return text.normalize('NFC').toLowerCase().replace(/[^\p{L}\p{N}]/gu, '');
}

const COMMANDS = new Map<string, string>();
for (const phrase of LINE_BREAK) COMMANDS.set(commandKey(phrase), '\n');
for (const phrase of PARAGRAPH) COMMANDS.set(commandKey(phrase), '\n\n');

/**
 * The line break an utterance asks for, or null if it is just words.
 *
 * Returned as the text to insert rather than as a name, because that is all the
 * caller does with it — and because it keeps "paragraph" from needing a second
 * branch anywhere else.
 */
export function spokenCommand(text: string): string | null {
  const key = commandKey(text);
  // A long utterance cannot be one of these, and skipping the lookup keeps a
  // dictated paragraph from being hashed on every interim result.
  if (!key || key.length > 24) return null;
  return COMMANDS.get(key) ?? null;
}
